import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router"
import { format } from "date-fns"
import {
  Ban,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Users,
  X,
} from "lucide-react"
import { useEffect, useState } from "react"
import { z } from "zod"
import { BookingsService } from "@/client"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DatePicker } from "@/components/ui/date-picker"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { parseLocalDate } from "@/lib/utils"

// Search params schema for URL state
const searchParamsSchema = z.object({
  search: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  status: z.string().optional(),
  page: z.coerce.number().optional(),
})

export const Route = createFileRoute("/_layout/bookings/")({
  component: BookingsPage,
  validateSearch: searchParamsSchema,
  staticData: {
    pageTitle: "Bookings",
    pageIcon: Calendar,
    pageIconClassName: "text-primary",
  },
  head: () => ({
    meta: [
      {
        title: "Bookings - Broken Islands Booking",
      },
    ],
  }),
})

const PAGE_SIZE = 20
const DEBOUNCE_MS = 300

/**
 * Map a booking status to a UI badge variant.
 *
 * @param status - Booking status, e.g. `"confirmed"`, `"pending"`, `"cancelled"`, or `"completed"`.
 * @returns `'default'` for `"confirmed"`, `'secondary'` for `"pending"` and unknown statuses, `'destructive'` for `"cancelled"`, and `'outline'` for `"completed"`.
 */
function getStatusBadgeVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "confirmed":
      return "default"
    case "pending":
      return "secondary"
    case "cancelled":
      return "destructive"
    case "completed":
      return "outline"
    default:
      return "secondary"
  }
}

/**
 * Convert an internal booking type identifier into a user-facing label.
 *
 * @param type - Internal booking type identifier (e.g., "lodge", "camping", "mixed")
 * @returns `'Lodge'`, `'Camping'`, or `'Mixed'` for recognized identifiers; otherwise returns the input `type` unchanged
 */
function getBookingTypeLabel(type: string): string {
  switch (type) {
    case "lodge":
      return "Lodge"
    case "camping":
      return "Camping"
    case "mixed":
      return "Mixed"
    default:
      return type
  }
}

/**
 * Renders a loading skeleton placeholder representing a booking card.
 *
 * Use this component while booking data is loading to show the expected card layout.
 *
 * @returns A JSX element containing a card-shaped skeleton used in the bookings list.
 */
function BookingCardSkeleton() {
  return (
    <Card className="hover:bg-accent/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
        <div className="mt-4 flex items-center gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Renders the Bookings page with search, date and status filters, debounced query updates, and paginated booking results.
 *
 * The component displays a filter bar (search, arrival-from/to, status), a results list with loading/error/empty states, and pagination controls. User interactions update URL search params and navigate to booking details or the new booking page.
 *
 * @returns The page element rendering the bookings list, filter controls, and pagination UI.
 */
function BookingsPage() {
  const router = useRouter()
  const navigate = useNavigate({ from: Route.fullPath })
  const searchParams = Route.useSearch()

  // Local state for debounced search
  const [searchInput, setSearchInput] = useState(searchParams.search ?? "")

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (searchParams.search ?? "")) {
        navigate({
          to: ".",
          search: {
            ...searchParams,
            search: searchInput || undefined,
            page: 1,
          },
        })
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput, searchParams, navigate])

  // Calculate offset from page
  const offset = ((searchParams.page ?? 1) - 1) * PAGE_SIZE

  // Fetch bookings
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "bookings",
      searchParams.search,
      searchParams.dateFrom,
      searchParams.dateTo,
      searchParams.status,
      offset,
    ],
    queryFn: () =>
      BookingsService.listBookings({
        search: searchParams.search || undefined,
        dateFrom: searchParams.dateFrom || undefined,
        dateTo: searchParams.dateTo || undefined,
        status: searchParams.status || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  })

  // Handlers for filters
  const handleDateFromChange = (date: Date | undefined) => {
    navigate({
      to: ".",
      search: {
        ...searchParams,
        dateFrom: date ? format(date, "yyyy-MM-dd") : undefined,
        page: 1,
      },
    })
  }

  const handleDateToChange = (date: Date | undefined) => {
    navigate({
      to: ".",
      search: {
        ...searchParams,
        dateTo: date ? format(date, "yyyy-MM-dd") : undefined,
        page: 1,
      },
    })
  }

  const handleStatusChange = (value: string) => {
    navigate({
      to: ".",
      search: {
        ...searchParams,
        status: value === "all" ? undefined : value,
        page: 1,
      },
    })
  }

  const handleClearFilters = () => {
    setSearchInput("")
    navigate({
      to: ".",
      search: {},
    })
  }

  const handlePageChange = (newPage: number) => {
    navigate({
      to: ".",
      search: {
        ...searchParams,
        page: newPage,
      },
    })
  }

  const handleBookingClick = (bookingId: string) => {
    router.navigate({ to: "/bookings/$id", params: { id: bookingId } })
  }

  // Pagination calculations
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0
  const currentPage = searchParams.page ?? 1

  // Check if any filters are active
  const hasActiveFilters =
    searchParams.search ||
    searchParams.dateFrom ||
    searchParams.dateTo ||
    searchParams.status

  return (
    <div className="container py-6">
      {/* Header */}
      <div className="flex items-center justify-end mb-6">
        <Button onClick={() => router.navigate({ to: "/bookings/new" })}>
          <Plus className="mr-2 h-4 w-4" />
          New Booking
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg border p-4 mb-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by guest name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Date From */}
          <DatePicker
            value={
              searchParams.dateFrom
                ? parseLocalDate(searchParams.dateFrom)
                : undefined
            }
            onChange={handleDateFromChange}
            placeholder="Arrival from"
          />

          {/* Date To */}
          <DatePicker
            value={
              searchParams.dateTo
                ? parseLocalDate(searchParams.dateTo)
                : undefined
            }
            onChange={handleDateToChange}
            placeholder="Arrival to"
          />

          {/* Status Filter */}
          <Select
            value={searchParams.status ?? "all"}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clear filters button */}
        {hasActiveFilters && (
          <div className="mt-4 flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-4 w-4" />
              Clear filters
            </Button>
            <span className="text-sm text-muted-foreground">
              {data?.total ?? 0} results
            </span>
          </div>
        )}
      </div>

      {/* Booking List */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <BookingCardSkeleton key={i} />
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">
              Failed to load bookings. Please try again.
            </p>
          </CardContent>
        </Card>
      ) : data?.data.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              {hasActiveFilters
                ? "No bookings match your filters."
                : "No bookings yet. Create your first booking to get started."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {data?.data.map((booking) => (
              <Card
                key={booking.id}
                className="hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => handleBookingClick(booking.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{booking.guest_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {booking.guest_email}
                      </p>
                    </div>
                    <Badge variant={getStatusBadgeVariant(booking.status)}>
                      {booking.status}
                    </Badge>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {format(parseLocalDate(booking.arrival_date), "MMM d")} -{" "}
                      {format(
                        parseLocalDate(booking.departure_date),
                        "MMM d, yyyy",
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {booking.number_of_guests} guest
                      {booking.number_of_guests !== 1 ? "s" : ""}
                    </span>
                    <Badge variant="outline">
                      {getBookingTypeLabel(booking.booking_type)}
                    </Badge>
                    {booking.cancelled_at && (
                      <span className="flex items-center gap-1 text-destructive">
                        <Ban className="h-4 w-4" />
                        Cancelled{" "}
                        {format(new Date(booking.cancelled_at), "MMM d, yyyy")}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && data && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, data.total)}{" "}
                of {data.total} bookings
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
